import React, { useState, useEffect } from 'react';
import { Page, Header, Content, InfoCard } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { HomePageStarredEntities } from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Grid, Typography, Box } from '@material-ui/core';
import { DetailsSidePanel } from './DetailsSidePanel';
import { EnhancedHomePageToolkit } from './EnhancedHomePageToolkit';

export interface NetworkDetails {
  masterNode?: string;
  workerNodes?: string;
  subnet?: string;
}

export interface QuickAccessLinkDetails {
  description?: string;
  service?: string;
  version?: string;
  contact?: string;
  networkDetails?: NetworkDetails;
  url?: string;
}

export interface QuickAccessLink {
  iconUrl: string;
  label: string;
  url: string;
  details?: QuickAccessLinkDetails;
}

interface QuickAccessItem {
  title: string;
  isExpanded: boolean;
  links: QuickAccessLink[];
}

const QuickAccess = () => {
  const [data, setData] = useState<QuickAccessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<QuickAccessLink | null>(null);
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { token } = await identityApi.getCredentials();
        const response = await fetch('/homepage/data.json', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [identityApi]);

  if (loading) {
    return <InfoCard title="Quick Access">Loading...</InfoCard>;
  }

  if (error) {
    return <InfoCard title="Quick Access">Error: {error}</InfoCard>;
  }

  const handleLinkClick = (link: QuickAccessLink) => {
    // Navigate directly to the URL
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const handleDetailsClick = (link: QuickAccessLink) => {
    // Open details in side panel
    setSelectedLink(link);
    setSidePanelOpen(true);
  };

  const handleCloseSidePanel = () => {
    setSidePanelOpen(false);
    setSelectedLink(null);
  };

  return (
    <>
      <InfoCard title="Quick Access" noPadding>
        {data.map((item) => (
          <EnhancedHomePageToolkit
            key={item.title}
            title={item.title}
            links={item.links}
            expanded={item.isExpanded}
            onLinkClick={handleLinkClick}
            onDetailsClick={handleDetailsClick}
          />
        ))}
      </InfoCard>

      {selectedLink && (
        <DetailsSidePanel
          open={sidePanelOpen}
          onClose={handleCloseSidePanel}
          title={selectedLink.label}
          url={selectedLink.url}
          iconUrl={selectedLink.iconUrl}
          details={selectedLink.details}
        />
      )}
    </>
  );
};

export const HomePage = () => {
  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Header title="Welcome to PostNord's Internal Developer Portal">
        </Header>
        <Content>
          <Box mb={4}>
            <HomePageSearchBar
              placeholder="Search services, documentation, APIs..."
            />
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <QuickAccess />
            </Grid>
            <Grid item xs={12} lg={4}>
              <HomePageStarredEntities />
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
